// v10/src/components/nav/AirportAutocomplete.js
//
// Autocomplete de aeródromos. Llama a GET /api/airports/search con debounce 300ms.
//
// Props:
//   - value: { icao, name, lat, lng } | null
//   - onChange: (airport | null) => void
//   - placeholder
//   - darkMode

import React, { useState, useEffect, useRef } from 'react';
import { Search, X, MapPin, Loader } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import navigationService from '../../services/navigationService';

const AirportAutocomplete = ({ value, onChange, placeholder, darkMode }) => {
  const { t } = useTranslation();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const debounceRef = useRef(null);
  const wrapperRef = useRef(null);

  // Cerrar dropdown al hacer click afuera
  useEffect(() => {
    const handler = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handler);
    document.addEventListener('touchstart', handler);
    return () => {
      document.removeEventListener('mousedown', handler);
      document.removeEventListener('touchstart', handler);
    };
  }, []);

  // Debounce 300ms — cancela el timer anterior si el usuario sigue tipeando,
  // así solo dispara la búsqueda cuando hay pausa real.
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (query.trim().length < 2) {
      setResults([]);
      return undefined;
    }
    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const r = await navigationService.searchAirports(query.trim(), 10);
        setResults(r.data || []);
      } catch {
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 300);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [query]);

  const handleSelect = (ap) => {
    onChange({
      icao: ap.icao_code,
      name: ap.name,
      lat: Number(ap.latitude),
      lng: Number(ap.longitude),
    });
    setQuery('');
    setResults([]);
    setShowDropdown(false);
  };

  const handleClear = () => {
    onChange(null);
    setQuery('');
    setResults([]);
  };

  // ── Styles ──────────────────────────────────────────────────────
  const text = darkMode ? 'white' : '#0f172a';
  const sub = darkMode ? 'rgba(255,255,255,0.55)' : '#64748b';
  const muted = darkMode ? 'rgba(255,255,255,0.4)' : '#94a3b8';
  const border = darkMode ? '1px solid rgba(255,255,255,0.08)' : '1px solid #e2e8f0';
  const bg = darkMode ? 'rgba(255,255,255,0.04)' : 'white';
  const dropdownBg = darkMode ? '#0f172a' : 'white';
  const itemHover = darkMode ? 'rgba(99,102,241,0.18)' : 'rgba(99,102,241,0.08)';

  // Pill cuando hay valor seleccionado
  if (value) {
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          padding: '10px 12px',
          borderRadius: 12,
          background: darkMode ? 'rgba(99,102,241,0.10)' : 'rgba(99,102,241,0.08)',
          border: '1px solid rgba(99,102,241,0.25)',
        }}
      >
        <MapPin style={{ width: 16, height: 16, color: '#6366f1', flexShrink: 0 }} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 14, fontWeight: 800, color: text, lineHeight: 1.2 }}>
            {value.icao}
          </div>
          <div
            style={{
              fontSize: 11,
              color: sub,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {value.name}
          </div>
        </div>
        <button
          type="button"
          onClick={handleClear}
          aria-label="Limpiar"
          style={{
            background: 'transparent',
            border: 'none',
            cursor: 'pointer',
            color: sub,
            padding: 4,
            borderRadius: 8,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <X style={{ width: 14, height: 14 }} />
        </button>
      </div>
    );
  }

  return (
    <div ref={wrapperRef} style={{ position: 'relative' }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          padding: '10px 12px',
          borderRadius: 12,
          background: bg,
          border,
        }}
      >
        <Search style={{ width: 14, height: 14, color: muted, flexShrink: 0 }} />
        <input
          type="text"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setShowDropdown(true);
          }}
          onFocus={() => setShowDropdown(true)}
          placeholder={placeholder || 'SAVY, Madryn, ...'}
          style={{
            flex: 1,
            border: 'none',
            outline: 'none',
            background: 'transparent',
            color: text,
            fontSize: 14,
            fontFamily: 'inherit',
            minWidth: 0,
          }}
        />
        {loading && <Loader className="spin" style={{ width: 14, height: 14, color: muted }} />}
      </div>

      {showDropdown && query.trim().length >= 2 && (
        <div
          style={{
            position: 'absolute',
            top: 'calc(100% + 4px)',
            left: 0,
            right: 0,
            zIndex: 120,
            background: dropdownBg,
            border,
            borderRadius: 12,
            boxShadow: '0 10px 30px rgba(0,0,0,0.18)',
            maxHeight: 280,
            overflowY: 'auto',
            padding: 4,
          }}
        >
          {loading && results.length === 0 ? (
            <div style={{ padding: 14, fontSize: 13, color: sub, textAlign: 'center' }}>
              {t('common_loading', { ns: 'common', defaultValue: 'Cargando…' })}
            </div>
          ) : results.length === 0 ? (
            <div style={{ padding: 14, fontSize: 13, color: sub, textAlign: 'center' }}>
              {t('nav_autocomplete_no_results', { ns: 'admin', defaultValue: 'Sin resultados' })}
            </div>
          ) : (
            results.map((ap) => (
              <button
                key={ap.id}
                type="button"
                onClick={() => handleSelect(ap)}
                style={{
                  width: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                  gap: 2,
                  padding: '9px 11px',
                  border: 'none',
                  background: 'transparent',
                  cursor: 'pointer',
                  borderRadius: 9,
                  textAlign: 'left',
                  color: text,
                  fontFamily: 'inherit',
                }}
                onMouseEnter={(e) => (e.currentTarget.style.background = itemHover)}
                onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
              >
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
                  <span style={{ fontSize: 13, fontWeight: 800 }}>{ap.icao_code}</span>
                  {ap.iata_code && (
                    <span style={{ fontSize: 11, color: muted }}>({ap.iata_code})</span>
                  )}
                  <span style={{ fontSize: 13, color: text, fontWeight: 500 }}>— {ap.name}</span>
                </div>
                {(ap.municipality || ap.elevation_ft != null) && (
                  <div style={{ fontSize: 11, color: sub, display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                    {ap.municipality && <span>{ap.municipality}</span>}
                    {ap.elevation_ft != null && (
                      <span>⛰ {ap.elevation_ft} ft</span>
                    )}
                  </div>
                )}
              </button>
            ))
          )}
        </div>
      )}

      <style>{`
        @keyframes airSpin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        .spin { animation: airSpin 1s linear infinite; }
      `}</style>
    </div>
  );
};

export default AirportAutocomplete;
